import { useMemo } from 'react';
import { useTheme } from '../theme/ThemeContext';
import { getTourTheme } from '../theme/themeStyles';

/**
 * The "Would you like a quick tour?" modal shown once on first visit.
 *
 * Three actions:
 *   - "Yes, show me around" → start the tour (parent handles).
 *   - "Maybe later" → dismiss for this session (parent decides whether to ask again next visit).
 *   - "Don't show again" → permanently mark the tour as seen so the prompt never reappears.
 *
 * The actual storage is owned by the parent via `tourStorage` from `TourGuide`. This component
 * is purely presentational so it can be reused / restyled independently of the storage scheme.
 */

interface TourPromptModalProps {
  open: boolean;
  onStart: () => void;
  onLater: () => void;
  onDontShowAgain: () => void;
}

export default function TourPromptModal({
  open,
  onStart,
  onLater,
  onDontShowAgain,
}: TourPromptModalProps) {
  const { isDark } = useTheme();
  const tt = useMemo(() => getTourTheme(isDark), [isDark]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[280] flex items-center justify-center px-4 py-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-prompt-heading"
    >
      <div className={tt.promptOverlay} aria-hidden onClick={onLater} />
      <div className="relative z-10 w-full max-w-md">
        <div className={tt.promptPanel}>
          <div className="flex items-start gap-3 mb-4">
            <div className={tt.promptIconWrap}>
              <i className="ri-route-line text-2xl" aria-hidden />
            </div>
            <div className="flex-1">
              <h2 id="tour-prompt-heading" className={tt.promptTitle}>
                Welcome to Digisaka
              </h2>
              <p className={tt.promptSub}>New here? We can show you around.</p>
            </div>
          </div>

          <p className={tt.promptBody}>
            Would you like a quick guided tour? We&apos;ll cover search, light/dark theme, the fields
            panel, analysis tools in the top bar, map controls, and Pandoy AI — about a minute.
          </p>

          <div className="mt-5 space-y-2">
            <button type="button" onClick={onStart} className={tt.promptPrimary}>
              <i className="ri-play-circle-line text-lg" aria-hidden />
              Yes, show me around
            </button>
            <div className="flex items-center gap-2">
              <button type="button" onClick={onLater} className={tt.promptSecondary}>
                Maybe later
              </button>
              <button type="button" onClick={onDontShowAgain} className={tt.promptTertiary}>
                Don&apos;t show again
              </button>
            </div>
          </div>

          <p className={tt.promptFootnote}>
            Relaunch anytime from the <i className="ri-question-line align-middle" aria-hidden /> Help
            button in the header.
          </p>
        </div>
      </div>
    </div>
  );
}
